/**
 * Store Zustand para gerenciamento de estado de Dispositivos
 */

import { create } from 'zustand';
import type { 
  Dispositivo, 
  DispositivoCompacto, 
  AdicionarDispositivoRequest,
  AceitarTermosRequest
} from '@/components/dispositivos/core/models';
import { RemoteDispositivoRepository } from '@/components/dispositivos/data/remote_dispositivo_repository';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';

interface DispositivosState {
  dispositivos: Dispositivo[];
  dispositivosCompactos: DispositivoCompacto[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchDispositivos: (compacto?: boolean) => Promise<void>;
  fetchDispositivoPorId: (id: string, compacto?: boolean) => Promise<Dispositivo | DispositivoCompacto>;
  adicionarDispositivo: (request: AdicionarDispositivoRequest) => Promise<Dispositivo>;
  aceitarTermos: (request: AceitarTermosRequest) => Promise<void>;
  removerDispositivo: (id: string) => Promise<void>;
  clearError: () => void;
}

const dataSource = new RemoteDataSource(Settings.urlBase);
const repository = new RemoteDispositivoRepository(dataSource);

export const useDispositivosStore = create<DispositivosState>((set, get) => ({
  dispositivos: [],
  dispositivosCompactos: [],
  isLoading: false,
  error: null,

  fetchDispositivos: async (compacto = false) => {
    set({ isLoading: true, error: null });
    try {
      const data = await repository.obterDispositivos(compacto);
      if (compacto) {
        set({ dispositivosCompactos: data as DispositivoCompacto[], isLoading: false });
      } else {
        set({ dispositivos: data as Dispositivo[], isLoading: false });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao buscar dispositivos',
        isLoading: false 
      });
    }
  },

  fetchDispositivoPorId: async (id: string, compacto = false) => {
    set({ isLoading: true, error: null });
    try {
      const dispositivo = await repository.obterDispositivoPorId(id, compacto);
      set({ isLoading: false });
      return dispositivo;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao buscar dispositivo',
        isLoading: false 
      });
      throw error;
    }
  },

  adicionarDispositivo: async (request: AdicionarDispositivoRequest) => {
    set({ isLoading: true, error: null });
    try {
      const dispositivo = await repository.adicionarDispositivo(request);
      set((state) => ({ 
        dispositivos: [...state.dispositivos, dispositivo],
        isLoading: false 
      }));
      return dispositivo;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao adicionar dispositivo',
        isLoading: false 
      });
      throw error;
    }
  },

  aceitarTermos: async (request: AceitarTermosRequest) => {
    set({ isLoading: true, error: null });
    try {
      await repository.aceitarTermos(request);
      set({ isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao aceitar termos',
        isLoading: false 
      });
      throw error;
    }
  },

  removerDispositivo: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await repository.removerDispositivo(id);
      set((state) => ({
        dispositivos: state.dispositivos.filter((d) => d.id !== id),
        dispositivosCompactos: state.dispositivosCompactos.filter((d) => d.id !== id),
        isLoading: false,
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Erro ao remover dispositivo',
        isLoading: false 
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
